import { APIResponse, Fetcher, FetchFunction } from '../../core';
import { createRequestUrl } from '../../core/fetcher/createRequestUrl';
import { Middleware } from '../middleware';

export const CSRF_HEADER = 'X-CSRF-Token';
export const SAFE_HTTP_METHODS: string[] = ['GET', 'HEAD', 'OPTIONS', 'TRACE'];

export type Token = string;

export type HttpTextResponse = {
    ok: boolean;
    text: () => Promise<string>;
};

type CsrfHttpClient = (url: string, method: 'POST') => Promise<HttpTextResponse>;

export class CsrfMiddleware implements Middleware<unknown> {
    constructor(
        private options: {
            attachExternalHeaderToRequests?: boolean;
            csrfEndpoint?: string;
            csrfHttpClient?: CsrfHttpClient;
        } = {},
    ) {}

    protected token: Token | undefined;

    protected async issueCsrfToken(): Promise<Token> {
        const url = `${this.options.csrfEndpoint || '/auth/csrf'}`;
        const csrfHttpClient = this.options.csrfHttpClient;
        const response = csrfHttpClient ? await csrfHttpClient(url, 'POST') : await fetch(url, { method: 'POST' });
        const responseBody = response.ok ? await response.text() : undefined;

        if (!response.ok || !responseBody) {
            throw new Error(`Can't issue CSRF token`);
        }

        return responseBody;
    }

    protected setCsrfToken(token: Token): void {
        this.token = token;
    }

    protected getCsrfToken(): Token | undefined {
        return this.token;
    }

    protected async configureCsrf(): Promise<Token> {
        const token = await this.issueCsrfToken();
        this.setCsrfToken(token);
        return token;
    }

    protected async verify(): Promise<string> {
        const token = this.getCsrfToken();

        if (token) return token;

        return this.configureCsrf();
    }

    async process(args: Fetcher.Args, next: FetchFunction): Promise<APIResponse<unknown, Fetcher.Error>> {
        try {
            args.headers = {
                ...args.headers,
                ...(this.options.attachExternalHeaderToRequests && { 'X-External': 'true' }),
                ...(!SAFE_HTTP_METHODS.includes(args.method) && { [CSRF_HEADER]: await this.verify() }),
            };
        } catch (exception) {
            let errorMessage = 'Unknown error';

            if (exception instanceof Error) {
                errorMessage = exception.message;
            }

            return {
                ok: false,
                error: {
                    reason: 'unknown',
                    errorMessage,
                },
                rawResponse: {
                    url: createRequestUrl(args.url, args.queryParameters),
                    statusText: errorMessage,
                    headers: new Headers(),
                    redirected: false,
                    type: 'error',
                    status: 0,
                },
            };
        }

        return next(args);
    }
}
